import React, { useEffect, useMemo, useRef } from 'react';
import styles from './SimpleTabloTicker.module.scss';
import classNames from 'classnames';
import { isAndroidTabloDevice } from '../../../utils/QueuePage/tabloDevice';

export type SimpleTickerItem = {
  id?: number | string;
  text: string;
};

type SimpleTabloTickerProps = {
  items: SimpleTickerItem[];
  /** Длительность одного полного пробега справа→налево (секунды). */
  durationSeconds?: number;
  className?: string;
  style?: React.CSSProperties;
  textClassName?: string;
  separator?: React.ReactNode;
  /** Android TV: шаги ~8/сек. Desktop: requestAnimationFrame. */
  lightMode?: boolean;
};

const isAndroidTablo = isAndroidTabloDevice();
const ANDROID_TICK_MS = 120;

/**
 * Классическая бегущая строка (не непрерывная лента):
 * появляется справа → полностью уходит влево → снова справа.
 */
export const SimpleTabloTicker = ({
  items,
  durationSeconds = 50,
  className,
  style,
  textClassName,
  separator,
  lightMode = isAndroidTablo
}: SimpleTabloTickerProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const segmentRef = useRef<HTMLSpanElement>(null);

  const texts = useMemo(
    () => items.map((i) => (i.text || '').trim()).filter(Boolean),
    [items]
  );

  const duration = Math.max(10, Number(durationSeconds) || 50);
  const textsKey = texts.join('\0');

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const segment = segmentRef.current;
    if (!viewport || !track || !segment) return;

    let pos = 0;
    let speedPxPerSec = 40;
    let textWidth = 0;
    let viewWidth = 0;
    let cancelled = false;
    let started = false;
    let rafId = 0;
    let intervalId = 0;
    let lastTs = 0;

    const apply = () => {
      track.style.transform = `translate3d(${Math.round(pos)}px, 0, 0)`;
    };

    const measureTextWidth = () => {
      const prev = track.style.transform;
      track.style.transform = 'none';
      void segment.offsetWidth;
      const w = Math.max(segment.offsetWidth, segment.scrollWidth);
      track.style.transform = prev || '';
      return Math.max(1, w);
    };

    const updateMetrics = () => {
      viewWidth = Math.max(1, viewport.clientWidth);
      textWidth = measureTextWidth();
      // Путь: от правого края до полного ухода за левый
      speedPxPerSec = (viewWidth + textWidth) / duration;
    };

    const startFromRight = () => {
      updateMetrics();
      pos = viewWidth;
      started = true;
      apply();
    };

    /** Сброс только когда правый край текста ушёл левее 0. */
    const wrapIfNeeded = () => {
      if (pos + textWidth <= 0) {
        pos = viewWidth;
      }
    };

    const tick = (dtSec: number) => {
      if (!started) return;
      pos -= speedPxPerSec * dtSec;
      wrapIfNeeded();
      apply();
    };

    rafId = window.requestAnimationFrame(() => {
      rafId = window.requestAnimationFrame(() => {
        if (!cancelled) startFromRight();
      });
    });

    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.ready) {
      void fonts.ready.then(() => {
        if (cancelled) return;
        updateMetrics();
        if (!started) startFromRight();
      });
    }

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (cancelled || !started) return;
            updateMetrics();
          })
        : null;
    ro?.observe(viewport);
    ro?.observe(segment);

    if (lightMode) {
      intervalId = window.setInterval(() => {
        if (cancelled) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          return;
        }
        tick(ANDROID_TICK_MS / 1000);
      }, ANDROID_TICK_MS);
    } else {
      const frame = (now: number) => {
        if (cancelled) return;
        rafId = window.requestAnimationFrame(frame);
        if (!started) {
          lastTs = now;
          return;
        }
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          lastTs = now;
          return;
        }
        const dt = lastTs ? Math.min(0.05, (now - lastTs) / 1000) : 0;
        lastTs = now;
        if (dt > 0) tick(dt);
      };
      rafId = window.requestAnimationFrame(frame);
    }

    return () => {
      cancelled = true;
      ro?.disconnect();
      if (intervalId) window.clearInterval(intervalId);
      if (rafId) window.cancelAnimationFrame(rafId);
      track.style.transform = '';
    };
  }, [duration, lightMode, textsKey]);

  if (texts.length === 0) {
    return null;
  }

  return (
    <div ref={viewportRef} className={classNames(styles.viewport, className)}>
      <div ref={trackRef} className={styles.track}>
        <span
          ref={segmentRef}
          className={classNames(styles.segment, textClassName)}
          style={style}
        >
          {texts.map((text, idx) => (
            <span className={styles.item} key={`${idx}-${text.slice(0, 24)}`}>
              {separator}
              <span>{text}</span>
            </span>
          ))}
          {/* Пауза перед следующим кругом: 100 пробелов после последней строки */}
          <span className={styles.tailGap} aria-hidden>
            {'\u00A0'.repeat(100)}
          </span>
        </span>
      </div>
    </div>
  );
};
